import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LeadTable from './LeadTable';
import { DeleteDialog, SuccessDialog, ErrorDialog } from './Dialogs';
import * as leadService from '../services/leadService';
import './LeadManagement.css';

const LeadManagement = () => {
  const navigate = useNavigate();
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortColumn, setSortColumn] = useState(3); // Contact ID column
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilter, setShowFilter] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [actionType, setActionType] = useState(''); // 'create', 'update', or 'delete'

  // Fetch leads from API
  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, sortColumn, sortDirection, columnFilters]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Map column indices to field names
      const fieldMap = {
        3: 'customerNo',
        4: 'customerName',
        5: 'mobNo',
        6: 'project',
        7: 'email',
        8: 'status',
        9: 'remarks',
        10: 'salesPerson',
        11: 'source',
        12: 'createdOn',
      };

      // Get sort column name
      const sortColumnName = fieldMap[sortColumn] || 'customerNo';

      // Build filter parameters matching API structure
      const params = {
        take: pageSize,
        skip: pageIndex * pageSize,
        sortColumn: sortColumnName,
        sortDirection: sortDirection === 'asc' ? 0 : 1,
        search: '',
        pageSize: pageSize,
        draw: pageIndex + 1,
        length: pageSize, // DataTables compatibility
        start: pageIndex * pageSize, // DataTables compatibility
      };

      // Add individual filter fields
      if (columnFilters[4]) params.customerName = columnFilters[4];
      if (columnFilters[5]) params.mobNo = columnFilters[5];
      if (columnFilters[6]) params.project = columnFilters[6];
      if (columnFilters[7]) params.email = columnFilters[7];
      if (columnFilters[8]) params.status = columnFilters[8];
      if (columnFilters[9]) params.remarks = columnFilters[9];
      if (columnFilters[10]) params.salesPerson = columnFilters[10];
      if (columnFilters[11]) params.source = columnFilters[11];
      if (columnFilters[12]) params.createdOn = columnFilters[12];

      console.log('Pagination params:', { pageIndex, pageSize, skip: pageIndex * pageSize, take: pageSize });
      console.log('Full API params:', params);

      const data = await leadService.getCustomerData(params);
      console.log('API Response:', { recordsTotal: data.recordsTotal, totalCount: data.totalCount, dataLength: data.data?.length });
      
      const total = data.recordsTotal || data.totalCount || 0;
      setTotalRecords(total);
      setFilteredLeads(data.data || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setErrorMessage('Error loading customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    // Call API to set session and redirect
    leadService
      .editCustomer(id)
      .then(() => {
        window.location.href = '/Client.aspx';
      })
      .catch((err) => {
        console.error('Edit error:', err);
        setErrorMessage('Error editing customer');
      });
  };

  const handleView = (id) => {
    leadService
      .viewCustomer(id)
      .then(() => {
        window.location.href = '/Client.aspx';
      })
      .catch((err) => {
        console.error('View error:', err);
        setErrorMessage('Error viewing customer');
      });
  };

  const handleDeleteClick = (id) => {
    setSelectedRowId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRowId) {
      deleteCustomer(selectedRowId);
    }
  };

  const deleteCustomer = async (id) => {
    try {
      const data = await leadService.deleteCustomer(id);

      if (data.d > 0) {
        setSuccessMessage('Success! Record has been deleted.');
        setActionType('delete');
        setDeleteDialogOpen(false);
        // Reload data after deletion
        setTimeout(() => {
          fetchLeads();
          setSuccessMessage('');
        }, 2000);
      } else {
        setErrorMessage('Error! Something went wrong. Try again.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setErrorMessage("Can't delete client");
    }
  };

  const handleAddNew = () => {
    navigate('/leads/add');
  };

  const handleColumnFilterChange = (colIndex, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [colIndex]: value,
    }));
    setPageIndex(0); // Reset to first page on filter change
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setPageIndex(0);
  };

  const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / pageSize) : 1;
  const hasNextPage = pageIndex < totalPages - 1;
  const hasPreviousPage = pageIndex > 0;

  return (
    <div className="lead-management">
      {/* Content Header */}
      <section className="content-header">
        <h1>Customer Management</h1>
        <ol className="breadcrumb">
          <li><a href="/">Home</a></li>
          <li className="active">Customer Management</li>
        </ol>
      </section>

      {/* Main Content */}
      <section className="content">
        <div className="box box-primary">
          <div className="box-body">
            <div className="row">
              <div className="col-md-4">
                <div className="checkbox">
                  <label className="plf">
                    <input
                      type="checkbox"
                      checked={showFilter}
                      onChange={(e) => setShowFilter(e.target.checked)}
                    />
                    <span className="cr">
                      <i className="cr-icon glyphicon glyphicon-ok"></i>
                    </span>
                    Filter <i className="glyphicon glyphicon-filter"></i>
                  </label>
                </div>
              </div>
              <div className="col-md-8 text-right">
                <button
                  className="btn btn-primary width-100 radius-20"
                  onClick={handleAddNew}
                >
                  <i className="fa fa-plus"></i>&nbsp;&nbsp;New
                </button>
              </div>

              {/* Status Messages */}
              <div className="col-md-12 mt-1">
                {actionType === 'create' && successMessage && (
                  <span className="badge bg-green" id="lblSuccess">
                    Success! Lead has been created.
                  </span>
                )}
                {actionType === 'update' && successMessage && (
                  <span className="badge bg-green" id="lblSuccessUpdate">
                    Success! Lead has been updated.
                  </span>
                )}
                {errorMessage && (
                  <span className="badge bg-red" id="lblError">
                    {errorMessage}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="box-body table-responsive">
            <LeadTable
              leads={filteredLeads}
              loading={loading}
              showFilter={showFilter}
              columnFilters={columnFilters}
              onColumnFilterChange={handleColumnFilterChange}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />

            {/* Pagination */}
            <div className="pagination-container">
              <button
                disabled={!hasPreviousPage}
                onClick={() => setPageIndex(pageIndex - 1)}
                className="btn btn-secondary"
              >
                <i className="fa fa-chevron-left"></i> Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i).map((page) => (
                  <button
                    key={page}
                    onClick={() => setPageIndex(page)}
                    className={`page-number ${pageIndex === page ? 'active' : ''}`}
                  >
                    {page + 1}
                  </button>
                ))}
              </div>

              <button
                disabled={!hasNextPage}
                onClick={() => setPageIndex(pageIndex + 1)}
                className="btn btn-secondary"
              >
                Next <i className="fa fa-chevron-right"></i>
              </button>
            </div>
            <div className="pagination-info">
              Total: {totalRecords} records | Showing: {filteredLeads.length} on page {pageIndex + 1} of {totalPages}
            </div>
          </div>
        </div>
      </section>

      {/* Dialogs */}
      {deleteDialogOpen && (
        <DeleteDialog
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteDialogOpen(false)}
        />
      )}

      {successMessage && (
        <SuccessDialog
          message={successMessage}
          onClose={() => {
            setSuccessMessage('');
            setActionType('');
          }}
        />
      )}

      {errorMessage && (
        <ErrorDialog
          message={errorMessage}
          onClose={() => setErrorMessage('')}
        />
      )}
    </div>
  );
};

export default LeadManagement;
